import { useLazyContext } from "@redotech/react-util/context";
import {
  ConversationPlatform,
  ExpandedConversation,
} from "@redotech/redo-model/conversation";
import { useMemo } from "react";
import { EmailIntegrationsContext } from "../services/email-integrations";

export interface EmailIntegrationStatus {
  isDisconnectedOrNoPermissions: boolean;
  errorMessage?: string;
}

/**
 * Hook to check if a conversation's email integration is connected and has proper permissions
 */
export function useEmailIntegrationStatus(
  conversation: ExpandedConversation,
): EmailIntegrationStatus {
  const [emailIntegrations] = useLazyContext(EmailIntegrationsContext);

  return useMemo<EmailIntegrationStatus>(() => {
    if (conversation.platform !== ConversationPlatform.EMAIL) {
      return { isDisconnectedOrNoPermissions: false };
    }

    if (!conversation.email?.email) {
      return { isDisconnectedOrNoPermissions: false };
    }

    if (!emailIntegrations.value) {
      return { isDisconnectedOrNoPermissions: false };
    }

    const conversationEmail = conversation.email.email.toLowerCase();
    const { gmailIntegrations, outlookIntegrations, customIntegrations } =
      emailIntegrations.value;

    const gmailIntegration = gmailIntegrations.find(
      (integration) => integration.email.toLowerCase() === conversationEmail,
    );

    if (gmailIntegration) {
      if (gmailIntegration.permissionsRevoked) {
        return {
          isDisconnectedOrNoPermissions: true,
          errorMessage:
            "Gmail integration has lost permissions. Please reconnect your Gmail account in email settings.",
        };
      }
      return { isDisconnectedOrNoPermissions: false };
    }

    const outlookIntegration = outlookIntegrations.find(
      (integration) => integration.email.toLowerCase() === conversationEmail,
    );

    if (outlookIntegration) {
      if (outlookIntegration.permissionsRevoked) {
        return {
          isDisconnectedOrNoPermissions: true,
          errorMessage:
            "Outlook integration has lost permissions. Please reconnect your Outlook account in email settings.",
        };
      }
      return { isDisconnectedOrNoPermissions: false };
    }

    const customIntegration = customIntegrations.find(
      (integration) => integration.email.toLowerCase() === conversationEmail,
    );

    if (customIntegration) {
      return { isDisconnectedOrNoPermissions: false };
    }

    return {
      isDisconnectedOrNoPermissions: true,
      errorMessage:
        "Email integration not found. The email account for this conversation is no longer connected.",
    };
  }, [conversation, emailIntegrations]);
}
